import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import useSWR from "swr";

import { api, getImpersonationUserId, type AnnouncementPublic, type AnnouncementSeverity, type AlertType } from "../services/api";
import { AnnouncementDrawer, noticeKind } from "./AnnouncementDrawer";
import { RichSummary } from "./RichSummary";
import {
  bannerStorage,
  forgetBannerChrome,
  readBannerChrome,
  rememberBannerChrome,
  type BannerChrome,
} from "./announcementNoticeState";
import { windowLabel } from "./statusAlerts";

/**
 * Announcements and service-status banners (Additional Features SoW v1.0,
 * Feature 1). Polls every 60s. The summary shown here is only
 * `body_excerpt_html`, clamped to two lines. "View full notice" opens the
 * drawer. Minimise and close are remembered per alert version.
 *
 * When an older API omits `body_html`, the stored body is plain text with
 * line breaks preserved. The browser does not parse Markdown.
 */
export function AnnouncementBanner() {
  const { data } = useSWR<AnnouncementPublic[]>(
    "/api/announcements/active",
    () => api.announcements.listActive(),
    { refreshInterval: 60_000, revalidateOnFocus: true },
  );

  if (!data || data.length === 0) return null;

  return (
    <div className="announcement-stack" role="region" aria-label="Announcements">
      {data.map((announcement, index) => (
        <AnnouncementNotice
          key={announcement.id}
          notice={noticeFromAnnouncement(announcement)}
          stackIndex={index}
          stackCount={data.length}
        />
      ))}
    </div>
  );
}

export interface AnnouncementNoticeModel {
  id: string;
  title: string;
  body: string;
  severity: AnnouncementSeverity;
  alertType: AlertType | null;
  windowStart: string | null;
  windowEnd: string | null;
  /** Null when the API omitted body_html. The banner then shows `body` as text. */
  bodyHtml: string | null;
  excerptHtml: string;
  truncated: boolean;
  /** `updated_at` from the API. A change brings the banner back. */
  version: string;
  lifecycleState: string | null;
}

export function noticeFromAnnouncement(announcement: AnnouncementPublic): AnnouncementNoticeModel {
  const html = announcement.body_html;
  return {
    id: announcement.id,
    title: announcement.title,
    body: announcement.body,
    severity: announcement.severity,
    alertType: announcement.alert_type,
    windowStart: announcement.window_start,
    windowEnd: announcement.window_end,
    bodyHtml: typeof html === "string" && html.length > 0 ? html : null,
    excerptHtml:
      typeof announcement.body_excerpt_html === "string" ? announcement.body_excerpt_html : "",
    truncated: announcement.body_truncated === true,
    version: announcement.updated_at || announcement.created_at,
    lifecycleState: announcement.lifecycle_state ?? null,
  };
}

/**
 * One banner. `preview` is the composer: always the full strip, same markup,
 * minimise and close visible but inert. The only full-notice control is
 * "Preview full notice", which opens the same drawer. It does not read or
 * write storage.
 */
export function AnnouncementNotice({
  notice,
  stackIndex = 0,
  stackCount = 1,
  mode = "live",
}: {
  notice: AnnouncementNoticeModel;
  stackIndex?: number;
  stackCount?: number;
  mode?: "live" | "preview";
}) {
  const preview = mode === "preview";
  const canClose = notice.severity !== "incident";
  const viewAs = !preview && viewingAsClient();
  const token = `${notice.version}|${stackIndex}|${stackCount}|${viewAs ? "s" : "l"}`;
  const [chrome, setChrome] = useState<BannerChrome>(() =>
    preview ? "full" : loadChrome(notice, stackIndex, stackCount, canClose, viewAs),
  );
  const [seen, setSeen] = useState(token);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const minimiseRef = useRef<HTMLButtonElement>(null);
  const expandRef = useRef<HTMLButtonElement>(null);
  const focusNext = useRef<"expand" | "minimise" | null>(null);

  if (!preview && seen !== token) {
    setSeen(token);
    setChrome(loadChrome(notice, stackIndex, stackCount, canClose, viewAs));
    setDrawerOpen(false);
  }

  const shown: BannerChrome = preview ? "full" : chrome;
  const showExcerpt = notice.bodyHtml !== null && notice.excerptHtml.length > 0;
  const showPlain = notice.bodyHtml === null && notice.body.length > 0;
  const overflow = useExcerptOverflow(
    bodyRef,
    shown === "full" && (showExcerpt || showPlain),
    showExcerpt ? notice.excerptHtml : notice.body,
  );

  useEffect(() => {
    const next = focusNext.current;
    focusNext.current = null;
    if (next === "expand") expandRef.current?.focus();
    if (next === "minimise") minimiseRef.current?.focus();
  }, [shown]);

  if (shown === "closed") return null;

  const bannerId = `ann-banner-${notice.id}`;
  const drawerId = `ann-drawer-${notice.id}`;
  const role = notice.severity === "incident" ? "alert" : "status";
  const win = windowLabel(notice.alertType, notice.windowStart, notice.windowEnd);
  const showViewFull = notice.truncated || overflow;
  const kind = noticeKind(notice.alertType, notice.severity);

  function storage() {
    return bannerStorage(viewAs);
  }

  function onMinimise() {
    if (preview) return;
    focusNext.current = "expand";
    rememberBannerChrome(storage(), notice.id, notice.version, "min");
    setChrome("min");
  }

  function onExpand() {
    if (preview) return;
    focusNext.current = "minimise";
    forgetBannerChrome(storage(), notice.id, notice.version);
    setChrome("full");
  }

  function onCloseNotice() {
    if (preview || !canClose) return;
    rememberBannerChrome(storage(), notice.id, notice.version, "closed");
    focusPageHeading();
    setChrome("closed");
  }

  return (
    <>
      <div
        id={bannerId}
        className={`announcement announcement-${notice.severity}${
          shown === "min" ? " announcement--min" : ""
        }`}
        role={role}
      >
        <div className="announcement-content">
          <div className="announcement-title">
            <span
              className={
                notice.severity === "incident" ? "banner-pulse-dot" : "announcement-dot"
              }
              aria-hidden="true"
            />
            {shown === "min" && <span className="announcement-kind">{kind}</span>}
            <span className="announcement-title-text">{notice.title}</span>
            <div className="announcement-actions">
              {shown === "min" ? (
                <button
                  type="button"
                  className="announcement-expand"
                  aria-expanded="false"
                  aria-controls={bannerId}
                  ref={expandRef}
                  onClick={onExpand}
                >
                  Expand
                </button>
              ) : (
                <button
                  type="button"
                  className="announcement-iconbtn"
                  aria-label="Minimise notice"
                  ref={minimiseRef}
                  onClick={onMinimise}
                >
                  <ChevronUp />
                </button>
              )}
              {canClose && (
                <button
                  type="button"
                  className="announcement-iconbtn"
                  aria-label="Close notice"
                  onClick={onCloseNotice}
                >
                  <CloseGlyph />
                </button>
              )}
            </div>
          </div>
          {shown === "full" && showExcerpt && (
            <RichSummary
              ref={bodyRef}
              className="announcement-body rt"
              html={notice.excerptHtml}
              variant="portal"
            />
          )}
          {shown === "full" && showPlain && (
            <div ref={bodyRef} className="announcement-body announcement-body-plain">
              {notice.body}
            </div>
          )}
          {shown === "full" && win && (
            <div className="announcement-window">
              <span className="l">{win.label}</span>
              <span className="v">{win.value}</span>
            </div>
          )}
          {shown === "full" && showViewFull && !preview && (
            <button
              type="button"
              className="announcement-more"
              aria-haspopup="dialog"
              aria-controls={drawerId}
              onClick={() => setDrawerOpen(true)}
            >
              View full notice
            </button>
          )}
          {preview && shown === "full" && (
            <button
              type="button"
              className="announcement-more"
              aria-haspopup="dialog"
              aria-controls={drawerId}
              onClick={() => setDrawerOpen(true)}
            >
              Preview full notice
            </button>
          )}
        </div>
      </div>
      {drawerOpen && (
        <AnnouncementDrawer
          id={drawerId}
          title={notice.title}
          body={notice.body}
          bodyHtml={notice.bodyHtml}
          severity={notice.severity}
          alertType={notice.alertType}
          windowStart={notice.windowStart}
          windowEnd={notice.windowEnd}
          version={notice.version}
          lifecycleState={notice.lifecycleState}
          onClose={() => setDrawerOpen(false)}
        />
      )}
    </>
  );
}

function loadChrome(
  notice: AnnouncementNoticeModel,
  index: number,
  count: number,
  canClose: boolean,
  viewAs: boolean,
): BannerChrome {
  return readBannerChrome(
    bannerStorage(viewAs),
    notice.id,
    notice.version,
    index,
    count,
    canClose,
  );
}

function viewingAsClient(): boolean {
  if (typeof getImpersonationUserId !== "function") return false;
  return Boolean(getImpersonationUserId());
}

function useExcerptOverflow(
  ref: RefObject<HTMLDivElement | null>,
  active: boolean,
  key: string,
): boolean {
  const [overflow, setOverflow] = useState(false);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!active || !node) {
      setOverflow(false);
      return;
    }
    const measure = () => {
      setOverflow(node.scrollHeight > node.clientHeight + 1);
    };
    measure();
    window.addEventListener("resize", measure);
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver === "function") {
      observer = new ResizeObserver(measure);
      observer.observe(node);
    }
    return () => {
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, [ref, active, key]);
  return overflow;
}

function focusPageHeading() {
  const heading = document.querySelector<HTMLElement>(
    "main h1, main .page-title, main .rf-h1",
  );
  const target = heading ?? document.querySelector<HTMLElement>("main");
  if (!target) return;
  if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
  target.focus();
}

function ChevronUp() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="18 15 12 9 6 15" />
    </svg>
  );
}

function CloseGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
